import { useEffect, useState } from 'react';

export type MarketSessionType = 'Live' | 'Closed' | 'PreMarket' | 'Weekend' | 'Holiday';

export interface MarketStatus {
  isOpen: boolean;
  statusLabel: string;
  subLabel: string;
  reason: string;
  session: MarketSessionType;
  isHoliday: boolean;
  holidayName?: string;
  istTimeString: string;
}

/**
 * Standard Fixed Annual National Holidays (Always closed every single year without exception).
 * Key format: "MM-DD"
 */
const FIXED_ANNUAL_HOLIDAYS: Record<string, string> = {
  '01-26': 'Republic Day',
  '04-14': 'Dr. Baba Saheb Ambedkar Jayanti',
  '05-01': 'Maharashtra Day',
  '08-15': 'Independence Day',
  '10-02': 'Mahatma Gandhi Jayanti',
  '12-25': 'Christmas',
};

/**
 * Year-specific Official NSE Trading Holidays Calendar (Capital Market Segment).
 * Format: "YYYY-MM-DD"
 */
const NSE_TRADING_HOLIDAYS: Record<string, string> = {
  // --- 2024 ---
  '2024-01-22': 'Special Ram Mandir Holiday',
  '2024-01-26': 'Republic Day',
  '2024-03-08': 'Mahashivratri',
  '2024-03-25': 'Holi',
  '2024-03-29': 'Good Friday',
  '2024-04-11': 'Id-Ul-Fitr (Ramzan Id)',
  '2024-04-14': 'Dr. Baba Saheb Ambedkar Jayanti',
  '2024-04-17': 'Shri Ram Navami',
  '2024-05-01': 'Maharashtra Day',
  '2024-05-20': 'General Parliamentary Elections (Mumbai)',
  '2024-06-17': 'Bakri Id / Id-Ul-Adha',
  '2024-07-17': 'Muharram',
  '2024-08-15': 'Independence Day',
  '2024-10-02': 'Mahatma Gandhi Jayanti',
  '2024-11-01': 'Diwali Laxmi Pujan (Muhurat Trading Only)',
  '2024-11-15': 'Gurunanak Jayanti',
  '2024-11-20': 'Maharashtra Assembly Elections',
  '2024-12-25': 'Christmas',

  // --- 2025 ---
  '2025-01-26': 'Republic Day',
  '2025-02-26': 'Mahashivratri',
  '2025-03-14': 'Holi',
  '2025-03-31': 'Id-Ul-Fitr',
  '2025-04-10': 'Shri Mahavir Jayanti',
  '2025-04-14': 'Dr. Baba Saheb Ambedkar Jayanti',
  '2025-04-18': 'Good Friday',
  '2025-05-01': 'Maharashtra Day',
  '2025-06-07': 'Bakri Id / Id-Ul-Adha',
  '2025-07-06': 'Muharram',
  '2025-08-15': 'Independence Day',
  '2025-08-27': 'Ganesh Chaturthi',
  '2025-10-02': 'Mahatma Gandhi Jayanti',
  '2025-10-21': 'Dussehra',
  '2025-10-22': 'Diwali Balipratipada',
  '2025-11-05': 'Gurunanak Jayanti',
  '2025-12-25': 'Christmas',

  // --- 2026 ---
  '2026-01-26': 'Republic Day',
  '2026-02-17': 'Mahashivratri',
  '2026-03-03': 'Holi',
  '2026-03-20': 'Id-Ul-Fitr',
  '2026-04-03': 'Good Friday',
  '2026-04-14': 'Dr. Baba Saheb Ambedkar Jayanti',
  '2026-04-21': 'Shri Ram Navami',
  '2026-05-01': 'Maharashtra Day',
  '2026-05-27': 'Bakri Id / Id-Ul-Adha',
  '2026-06-25': 'Muharram',
  '2026-08-15': 'Independence Day',
  '2026-08-25': 'Milad-un-Nabi',
  '2026-09-14': 'Ganesh Chaturthi',
  '2026-10-02': 'Mahatma Gandhi Jayanti',
  '2026-10-20': 'Dussehra',
  '2026-11-08': 'Diwali Balipratipada',
  '2026-11-24': 'Gurunanak Jayanti',
  '2026-12-25': 'Christmas',

  // --- 2027 ---
  '2027-01-26': 'Republic Day',
  '2027-03-08': 'Mahashivratri',
  '2027-03-22': 'Holi',
  '2027-03-26': 'Good Friday',
  '2027-04-14': 'Dr. Baba Saheb Ambedkar Jayanti',
  '2027-05-01': 'Maharashtra Day',
  '2027-08-15': 'Independence Day',
  '2027-10-02': 'Mahatma Gandhi Jayanti',
  '2027-12-25': 'Christmas',
};

/**
 * Converts any Date object to exact Indian Standard Time (IST = UTC + 5:30) components.
 */
export const getIstComponents = (
  date: Date = new Date()
): {
  year: number;
  month: number;
  day: number;
  dayOfWeek: number;
  hours: number;
  minutes: number;
  seconds: number;
  isoDate: string;
  monthDay: string;
  timeString: string;
} => {
  // Use Intl with Asia/Kolkata for exact timezone computation
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const getVal = (type: string) => parts.find((p) => p.type === type)?.value || '0';

    const year = parseInt(getVal('year'), 10);
    const month = parseInt(getVal('month'), 10);
    const day = parseInt(getVal('day'), 10);
    const hours = parseInt(getVal('hour'), 10);
    const minutes = parseInt(getVal('minute'), 10);
    const seconds = parseInt(getVal('second'), 10);

    // Calculate dayOfWeek using the local year, month - 1, day
    const istDateObj = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
    const dayOfWeek = istDateObj.getUTCDay();

    const pad = (n: number) => String(n).padStart(2, '0');
    const isoDate = `${year}-${pad(month)}-${pad(day)}`;
    const monthDay = `${pad(month)}-${pad(day)}`;
    const timeString = `${pad(hours)}:${pad(minutes)}`;

    return {
      year,
      month,
      day,
      dayOfWeek,
      hours,
      minutes,
      seconds,
      isoDate,
      monthDay,
      timeString,
    };
  } catch {
    // Fallback UTC + 5.5 hours calculation if Intl is unavailable
    const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;
    const ist = new Date(utcTime + 3600000 * 5.5);
    const pad = (n: number) => String(n).padStart(2, '0');
    const year = ist.getFullYear();
    const month = ist.getMonth() + 1;
    const day = ist.getDate();
    const isoDate = `${year}-${pad(month)}-${pad(day)}`;
    const monthDay = `${pad(month)}-${pad(day)}`;
    const timeString = `${pad(ist.getHours())}:${pad(ist.getMinutes())}`;

    return {
      year,
      month,
      day,
      dayOfWeek: ist.getDay(),
      hours: ist.getHours(),
      minutes: ist.getMinutes(),
      seconds: ist.getSeconds(),
      isoDate,
      monthDay,
      timeString,
    };
  }
};

/**
 * Checks whether a given date is an official NSE trading holiday.
 */
export const checkNseTradingHoliday = (
  date: Date = new Date()
): { isHoliday: boolean; holidayName?: string } => {
  const { isoDate, monthDay } = getIstComponents(date);

  // 1. Exact year-specific calendar match
  if (NSE_TRADING_HOLIDAYS[isoDate]) {
    return { isHoliday: true, holidayName: NSE_TRADING_HOLIDAYS[isoDate] };
  }

  // 2. Fixed annual recurring national holiday match
  if (FIXED_ANNUAL_HOLIDAYS[monthDay]) {
    return { isHoliday: true, holidayName: FIXED_ANNUAL_HOLIDAYS[monthDay] };
  }

  return { isHoliday: false };
};

/**
 * Evaluates the current NSE / Indian Stock Market status with holiday, weekend, and session awareness.
 */
export const getNseMarketStatus = (date: Date = new Date()): MarketStatus => {
  const ist = getIstComponents(date);
  const holidayCheck = checkNseTradingHoliday(date);

  // 1. National or Exchange Trading Holiday
  if (holidayCheck.isHoliday) {
    return {
      isOpen: false,
      statusLabel: 'NSE Closed',
      subLabel: holidayCheck.holidayName || 'Trading Holiday',
      reason: `Trading Holiday: ${holidayCheck.holidayName}`,
      session: 'Holiday',
      isHoliday: true,
      holidayName: holidayCheck.holidayName,
      istTimeString: ist.timeString,
    };
  }

  // 2. Weekend (Saturday = 6, Sunday = 0)
  if (ist.dayOfWeek === 0 || ist.dayOfWeek === 6) {
    return {
      isOpen: false,
      statusLabel: 'NSE Closed',
      subLabel: ist.dayOfWeek === 6 ? 'Saturday' : 'Sunday',
      reason: 'Weekend: Markets Closed',
      session: 'Weekend',
      isHoliday: false,
      istTimeString: ist.timeString,
    };
  }

  const totalMinutes = ist.hours * 60 + ist.minutes;

  // 3. Pre-market session (09:00 to 09:15 IST)
  if (totalMinutes >= 9 * 60 && totalMinutes < 9 * 60 + 15) {
    return {
      isOpen: false,
      statusLabel: 'Pre-Market',
      subLabel: 'Discovery',
      reason: 'Pre-Market Discovery (09:00–09:15 IST)',
      session: 'PreMarket',
      isHoliday: false,
      istTimeString: ist.timeString,
    };
  }

  // 4. Regular Continuous Trading Session (09:15 to 15:30 IST)
  if (totalMinutes >= 9 * 60 + 15 && totalMinutes <= 15 * 60 + 30) {
    return {
      isOpen: true,
      statusLabel: 'NSE Live',
      subLabel: 'IST',
      reason: 'Market Active (09:15–15:30 IST)',
      session: 'Live',
      isHoliday: false,
      istTimeString: ist.timeString,
    };
  }

  // 5. Post-market / Closed on Weekday
  const isPostMarket = totalMinutes > 15 * 60 + 30;
  return {
    isOpen: false,
    statusLabel: 'NSE Closed',
    subLabel: isPostMarket ? 'Post-Market' : 'Opens 09:15',
    reason: isPostMarket ? 'Market Closed at 15:30 IST' : 'Pre-market Opens 09:00 IST',
    session: 'Closed',
    isHoliday: false,
    istTimeString: ist.timeString,
  };
};

/**
 * Backward compatible boolean check for market hours.
 */
export const isMarketHours = (date: Date = new Date()): boolean => {
  return getNseMarketStatus(date).isOpen;
};

/**
 * React Hook that subscribes to the live Indian market status,
 * refreshing every 15 seconds so changes happen automatically without manual page reload.
 */
export const useMarketStatus = (): MarketStatus => {
  const [status, setStatus] = useState<MarketStatus>(() => getNseMarketStatus());

  useEffect(() => {
    const update = () => setStatus(getNseMarketStatus());
    update();
    const timer = setInterval(update, 15000);
    return () => clearInterval(timer);
  }, []);

  return status;
};
